import {
  FileCreateParams,
  FileObject,
  FilePurpose,
  FilesApi,
  FileType,
} from '@citruscamps/citrus-client'
import { faImage, faSpinner, faUpload } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useMemo, useState } from 'react'
import { DropEvent, DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone'
import { useUploadFiles } from '../../hooks/useUploadFiles'
import { generateApiConfig } from '../../utils/client-config'
import { useFetchInfiniteFiles } from '../MediaSelect/hooks/useFetchFiles'
import './Dropzone.scss'
import { FileList } from '../FileList/FileList'

export type DropzoneFile =
  | (Partial<Pick<FileObject, 'id' | 'url'>> &
      Omit<FileObject, 'id' | 'object' | 'url' | 'created' | 'created_by'>)
  | FileCreateParams

const isValidFileType = (value: string): value is FileType => Object.keys(FileType).includes(value)

const activeStyle = {
  borderColor: '#2a7953',
}

const acceptStyle = {
  borderColor: '#2a7953',
}

const rejectStyle = {
  borderColor: '#e63757',
}

export interface DropzoneProps extends DropzoneOptions {
  files?: Array<DropzoneFile | string>
  maxFileCount?: number
  icon: 'file' | 'image'
  context: string
  purpose?: FilePurpose
  uploadOnDrop?: boolean
  style?: React.CSSProperties
  onUpload?: (files: DropzoneFile[]) => void
  onRemove?: (file: DropzoneFile, index: number) => void | Promise<void>
  onChange?: (files: DropzoneFile[]) => void
}

export const Dropzone = ({
  files: initialFiles,
  context,
  maxFileCount,
  onDrop: handleDrop,
  onRemove: handleRemove,
  onUpload: handleUpload,
  onChange: handleChange,
  children = 'Drag your file here, or click to upload them',
  ...props
}: React.PropsWithChildren<DropzoneProps>): React.ReactElement => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isControlled = typeof initialFiles !== 'undefined'
  const [fileObjects, fileIds] = (initialFiles || []).reduce(
    ([e, c]: [DropzoneFile[], string[]], fileOrId) => {
      if (!fileOrId) return [e, c]
      if (typeof fileOrId === 'string') return [e, [...c, fileOrId]]
      return [[...e, fileOrId], c]
    },
    [[], []],
  )
  const [uncontrolledFiles, setUncontrolledFiles] = useState<DropzoneFile[]>([])
  const { data: fetchedFiles } = useFetchInfiniteFiles({
    programId: context,
    fetchProps: {
      filter: {
        id: fileIds,
      },
    },
    enabled: fileObjects.length === 0 && !!fileIds && fileIds.length > 0,
  })
  const controlledFiles: DropzoneFile[] = useMemo(() => {
    if (fileObjects.length === 0 && fileIds.length > 0) return fetchedFiles
    return fileObjects
  }, [fileObjects, fileIds.length, fetchedFiles])
  const files = isControlled ? controlledFiles : uncontrolledFiles
  const setFiles = handleChange ?? setUncontrolledFiles
  const { handleBulkUpload } = useUploadFiles({ context })
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    ...props,
    onDrop: async (
      acceptedFiles: File[],
      rejectedFiles: FileRejection[],
      event: DropEvent,
    ): Promise<void> => {
      setIsSubmitting(true)
      const oldFiles: DropzoneFile[] = [...files]
      let newFiles = [
        ...acceptedFiles
          .map((file: File): DropzoneFile | undefined =>
            !isValidFileType(file.type)
              ? undefined
              : {
                  purpose: props?.purpose || 'other',
                  filename: file.name,
                  filetype: file.type,
                  size: file.size,
                  context,
                  value: file,
                },
          )
          .filter((file): file is DropzoneFile => Boolean(file)),
        ...oldFiles,
      ].slice(0, maxFileCount)
      handleDrop?.(acceptedFiles, rejectedFiles, event)
      let uploadedFiles: DropzoneFile[] = []
      if (props?.uploadOnDrop) {
        try {
          uploadedFiles = await handleBulkUpload(acceptedFiles, {
            purpose: props?.purpose || 'other',
          })
        } catch {}
        await handleUpload?.(uploadedFiles)
      } else {
        uploadedFiles = (
          await Promise.all(
            acceptedFiles.map(async (file: File): Promise<DropzoneFile | undefined> => {
              if (!isValidFileType(file.type)) return undefined
              const reader = new FileReader()
              const promise = new Promise<ProgressEvent<FileReader>>((resolve) => {
                reader.onload = resolve
              })
              reader.readAsDataURL(file)
              const readerEvent: ProgressEvent<FileReader> = await promise
              let url: string | undefined
              if (readerEvent?.target?.result) {
                if (typeof readerEvent.target.result === 'string') {
                  url = readerEvent.target.result
                } else {
                  const bufView: number[] = []
                  new Uint16Array(readerEvent.target.result)
                    .map((i) => i)
                    .forEach((i) => bufView.push(i))
                  url = String.fromCharCode.apply(null, bufView)
                }
              }
              if (!url) return undefined
              return {
                url,
                purpose: props.purpose || 'other',
                filename: file.name,
                filetype: file.type,
                size: file.size,
                context,
              }
            }),
          )
        ).filter((file): file is DropzoneFile => Boolean(file))
      }
      newFiles = [...uploadedFiles, ...oldFiles].slice(0, maxFileCount)
      setFiles(newFiles)
      setIsSubmitting(false)
    },
  })

  const isMaxUploaded = maxFileCount && files.length >= maxFileCount
  const style = useMemo<React.CSSProperties>(
    () => ({
      ...props.style,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(isMaxUploaded
        ? {
            display: 'none',
          }
        : {}),
    }),
    [isDragAccept, isDragActive, isDragReject, isMaxUploaded, props.style],
  )
  return (
    <>
      <div {...getRootProps({ className: 'dropzone position-relative', style })}>
        <input {...getInputProps()} />
        <p className="mt-2" style={isSubmitting ? { opacity: 0 } : undefined}>
          {props?.icon === 'image' ? (
            <FontAwesomeIcon icon={faImage} size="2x" />
          ) : (
            <FontAwesomeIcon icon={faUpload} size="2x" />
          )}
        </p>
        <div className="mb-2" style={{ ...(isSubmitting ? { opacity: 0 } : {}) }}>
          {children}
        </div>
        {isSubmitting && (
          <div className="position-absolute top-0 w-100 text-center mt-4">
            <FontAwesomeIcon icon={faSpinner} pulse size="2x" />
          </div>
        )}
      </div>
      <FileList
        files={files}
        onRemove={async (file: DropzoneFile, index: number) => {
          await handleRemove?.(file, index)
          setFiles(files.filter((_, i) => i !== index))
          if (!isControlled && 'id' in file && file.id) {
            const fileClient = new FilesApi(generateApiConfig())
            await fileClient.deleteFile({ id: file.id })
          }
        }}
      />
    </>
  )
}
